// Existing/Proposed state matrix model. Per the spec, the matrix is a *view of
// objects*: every cell is a computational object (area + classification + status
// + source + confidence), not a bare number. Existing and Proposed share this
// same shape so they can be compared directly.
import { uid, STATUS } from './model';

// Area classification (סיווג שטח) — drives the rights engine later.
export const AREA_CLASS = {
  MAIN: 'main',          // עיקרי
  SERVICE: 'service',    // שירות
  PARKING: 'parking',    // חניה
  OPEN: 'open',          // פתוח
  UNCOUNTED: 'uncounted',// לא נספר
};

export const AREA_CLASS_OPTIONS = [
  { id: AREA_CLASS.MAIN, label: 'עיקרי' },
  { id: AREA_CLASS.SERVICE, label: 'שירות' },
  { id: AREA_CLASS.PARKING, label: 'חניה' },
  { id: AREA_CLASS.OPEN, label: 'פתוח' },
  { id: AREA_CLASS.UNCOUNTED, label: 'לא נספר' },
];

export function areaClassLabel(id) {
  return (AREA_CLASS_OPTIONS.find((o) => o.id === id) || {}).label || '';
}

export function makeColumn(key, label, classification) {
  return { id: uid('col'), key, label, classification };
}

export function makeRow(init = {}) {
  return { id: uid('row'), level: init.level ?? null, label: init.label || '' };
}

// Default element columns matching the spec's example matrix.
export function defaultColumns() {
  return [
    makeColumn('apartments', 'דירות', AREA_CLASS.MAIN),
    makeColumn('stairs', 'מדרגות', AREA_CLASS.SERVICE),
    makeColumn('elevator', 'מעלית', AREA_CLASS.SERVICE),
    makeColumn('lobby', 'מבואה', AREA_CLASS.SERVICE),
    makeColumn('parking', 'חניה', AREA_CLASS.PARKING),
    makeColumn('other', 'אחר', AREA_CLASS.UNCOUNTED),
  ];
}

export function makeMatrix(init = {}) {
  return {
    columns: init.columns || defaultColumns(),
    rows: init.rows || [makeRow({ level: 0, label: 'קרקע' })],
    cells: init.cells || {}, // sparse: keyed by cellKey(rowId, colId)
  };
}

export function cellKey(rowId, colId) {
  return `${rowId}::${colId}`;
}

export function emptyCell() {
  return { sqm: null, status: STATUS.MISSING, source: null, confidence: null, note: null };
}

export function getCell(matrix, rowId, colId) {
  return matrix.cells[cellKey(rowId, colId)] || emptyCell();
}

// ---- totals ------------------------------------------------------------------
function cellSqm(matrix, rowId, colId) {
  const c = matrix.cells[cellKey(rowId, colId)];
  const v = c && c.sqm;
  return Number.isFinite(v) ? v : 0;
}

export function rowTotal(matrix, rowId) {
  return matrix.columns.reduce((s, col) => s + cellSqm(matrix, rowId, col.id), 0);
}

export function colTotal(matrix, colId) {
  return matrix.rows.reduce((s, row) => s + cellSqm(matrix, row.id, colId), 0);
}

export function grandTotal(matrix) {
  return matrix.rows.reduce((s, row) => s + rowTotal(matrix, row.id), 0);
}

// Totals grouped by area classification — what the rights engine consumes.
export function classTotals(matrix) {
  const out = { main: 0, service: 0, parking: 0, open: 0, uncounted: 0 };
  for (const col of matrix.columns) {
    const t = colTotal(matrix, col.id);
    if (out[col.classification] != null) out[col.classification] += t;
  }
  return out;
}
